import { _marketSegmentId } from "../chart-brokerapi/broker.helper"
import { getExchange } from "../utils/helpers"
import { ScripSearchedData } from "./symbolData"

export interface globalSearch {
    status: string
    message: string
    data: Data
}

export interface Data {
    took: number
    timed_out: boolean
    _shards: Shards
    hits: Hits
}

export interface Shards {
    total: number
    successful: number
    skipped: number
    failed: number
}

export interface Hits {
    total: Total
    max_score: number
    hits: Hit[]
}

export interface Total {
    value: number
    relation: string
}

export interface Hit {
    _index: string
    _id: string
    _score: number
    _source: globalSearchItem
    _type: string
    sort: any
}

export interface globalSearchItem {
    asset_type: string
    custom_score: number
    eligibility: number
    expiry_date: number
    has_icon: boolean
    has_option_chain: boolean
    industry: string
    instrument_name: string
    isin_code: string
    lot_size: number
    market_segment_id: number
    option_type: string
    search_summary: string
    sector_name: string
    security_desc: string
    series: string
    strike_price: number
    symbol: string
    tick: number
    token: number
}

// Define a type for the return value of the function
type DateFormats = { format1: string, format2: string };

function convertTimestampToFormats(timestamp: number): DateFormats {
    // Check for the edge case where the timestamp is 0
    if (timestamp === 0) {
        return { format1: '', format2: '' }; // Return empty strings
    }

    // Create a Date object from the Unix timestamp (multiply by 1000 to convert to milliseconds)
    const date = new Date(timestamp * 1000);

    // Get the day, month (in short), and year
    const day = date.getDate();
    const month = date.toLocaleString('en-US', { month: 'short' }).toUpperCase(); // Short month (e.g., "JUL")
    const year = date.getFullYear();

    // Format "31JUL2025"
    const format1 = `${day.toString().padStart(2, '0')}${month}${year}`;

    // Format "JUL31"
    const format2 = `${month}${day.toString().padStart(2, '0')}`;

    return { format1, format2 };
}

export function processScripSearchedData(item: Hit, exchange: string, symbolType: number): ScripSearchedData | null {
    const formattedDates: DateFormats = convertTimestampToFormats(item._source.expiry_date);
    let result: ScripSearchedData = {
        data_status: "",
        description: "",
        exchange: "",
        exchange_logo: "",
        full_name: "",
        name: "",
        sort: [],
        symbol: "",
        ticker: "",
        type: item._type,
        _id: item._source.market_segment_id + '-' + item._source.token,
        _index: item._index,
        _score: item._score,
        _source: {
            DecimalLocator: item._source.market_segment_id !== 13 ? 100 : 10000000,
            ExchangeSegment: _marketSegmentId(item._source.market_segment_id.toString()),
            MktSegId: item._source.market_segment_id,
            exp: item._source.expiry_date ? convertTimestampToISO(item._source.expiry_date) : '',
            id: "",
            nExpiryDate: 0,
            nExpiryDate1: formattedDates.format1,
            nExpiryDate2: formattedDates.format2,
            nFOExists: item._source.has_option_chain ? 1 : 0,
            nInstrumentType: 0,
            nIsIndex: item._source.instrument_name === "EQIDX" ? 1 : 0,
            nMarketSegmentId: item._source.market_segment_id,
            nRegularLot: item._source.lot_size,
            nStrikePrice: item._source.strike_price ? item._source.strike_price.toString() : "",
            nStrikePrice1: item._source.strike_price ? (item._source.strike_price / (item._source.market_segment_id !== 13 ? 100 : 10000000)).toFixed(2) : "",
            nToken: item._source.token,
            sExchange: getExchange(item._source.market_segment_id),
            sInstrumentName: item._source.instrument_name,
            sOptionType: item._source.option_type,
            sSecurityDesc: item._source.security_desc,
            sSeries: item._source.series,
            sSymbol: item._source.symbol,
        },
        _type: item._type,
    };

    // Step 1: Modify based on market segment
    if (result._source.nMarketSegmentId == 8) {
        result._source.nMarketSegmentId = 3;
    }

    // if (exchange)
    // Step 3: Set 'full_name', 'exchange', and 'data_status'
    result["full_name"] = String(result._source.sSecurityDesc);
    result["exchange"] = String(result._source.sExchange);
    result["data_status"] = "streaming";
    if (exchange != 'All Exchanges') {
        if (result["exchange"].toUpperCase() != exchange.toUpperCase()) {
            return null; // Return null if the exchange does not match
        }
    }
    // Step 4: Set exchange logo based on exchange
    switch (result._source.sExchange) {
        case "NSE":
            result["exchange_logo"] = "https://static.rupeezy.in/stock_images/svgs/NSE.svg";
            break;
        case "BSE":
            result["exchange_logo"] = "https://static.rupeezy.in/stock_images/svgs/BSE.svg";
            break;
        case "MCX":
            result["exchange_logo"] = "https://static.rupeezy.in/stock_images/svgs/MCX.svg";
            break;
    }

    // Step 5: Set values for 'type', 'symbol', 'name', 'ticker', and 'description' based on instrument name
    switch (result._source.sInstrumentName) {
        case 'EQIDX':
            result["symbol"] = result._source.sExchange + ":" + result._source.sSymbol + "IDX";
            result["ticker"] = result._source.sExchange + ":" + result._source.sSymbol + "IDX";
            result["name"] = result._source.sExchange + ":" + result._source.sSymbol + "IDX";
            result["type"] = "Index";
            result["description"] = result._source.sSymbol + " Index";
            result["logo_urls"] = [result["exchange_logo"]];
            break;

        case 'EQUITIES':
            result["symbol"] = result._source.sExchange + ":" + result._source.sSymbol;
            result["ticker"] = result._source.sExchange + ":" + result._source.sSymbol;
            result["name"] = result._source.sExchange + ":" + result._source.sSymbol;
            result["type"] = "Stock";
            result["description"] = String(result._source.sSecurityDesc);
            result["logo_urls"] = [`https://static.rupeezy.in/stock_images/svgs/${result._source.sSymbol?.toUpperCase()}.svg`];
            break;
        case 'OPTSTK':
            result["symbol"] = result._source.sExchange + ":" + result._source.sSecurityDesc;
            result["ticker"] = result._source.sExchange + ":" + result._source.sSecurityDesc;
            result["name"] = result._source.sExchange + ":" + result._source.sSecurityDesc;
            result["type"] = "Options";
            result["description"] = result._source.sSymbol + " " + result._source.nExpiryDate1?.slice(-2) + " " + result._source.nExpiryDate2 + " " + parseFloat(String(result._source.nStrikePrice)) / Number(result._source.DecimalLocator) + " " + result._source.sOptionType;
            result["logo_urls"] = [`https://static.rupeezy.in/stock_images/svgs/${result._source.sSymbol?.toUpperCase()}.svg`];
            break;
        case 'FUTSTK':
            result["logo_urls"] = [`https://static.rupeezy.in/stock_images/svgs/${result._source.sSymbol?.toUpperCase()}.svg`];
            result["symbol"] = result._source.sExchange + ":" + result._source.sSecurityDesc;
            result["ticker"] = result._source.sExchange + ":" + result._source.sSecurityDesc;
            result["name"] = result._source.sExchange + ":" + result._source.sSecurityDesc;
            result["type"] = "Futures";
            result["description"] = result._source.sSymbol + " " + result._source.nExpiryDate1?.slice(-2) + " " + result._source.nExpiryDate2;
            break;
        case 'FUTIDX':
            result["symbol"] = result._source.sExchange + ":" + result._source.sSecurityDesc;
            result["ticker"] = result._source.sExchange + ":" + result._source.sSecurityDesc;
            result["name"] = result._source.sExchange + ":" + result._source.sSecurityDesc;
            result["type"] = "Futures";
            result["description"] = result._source.sSymbol + " " + result._source.nExpiryDate1?.slice(-2) + " " + result._source.nExpiryDate2;
            result["logo_urls"] = [result["exchange_logo"]];
            break;
        case 'OPTIDX':
            result["symbol"] = result._source.sExchange + ":" + result._source.sSecurityDesc;
            result["ticker"] = result._source.sExchange + ":" + result._source.sSecurityDesc;
            result["name"] = result._source.sExchange + ":" + result._source.sSecurityDesc;
            result["type"] = "Options";
            result["description"] = result._source.sSymbol + " " + result._source.nExpiryDate1?.slice(-2) + " " + result._source.nExpiryDate2 + " " + parseInt(String(result._source.nStrikePrice)) / Number(result._source.DecimalLocator) + " " + result._source.sOptionType;
            result["logo_urls"] = [result["exchange_logo"]];
            break;
        case 'FUTCOM':
            var exp_date = new Date(String(result._source.exp));
            let data = result._source.sExchange + ":" + result._source.sSymbol + result._source.nExpiryDate1?.toString().slice(-2) + exp_date.toLocaleString('default', { month: 'short', timeZone: 'UTC' }).toUpperCase().slice(0, 3) + "FUT";
            result["symbol"] = data;
            result["ticker"] = data;
            result["name"] = data;
            result["type"] = "Futures";
            result["description"] = result._source.sSymbol + " " + result._source.nExpiryDate1 + " FUT";
            result["logo_urls"] = [`https://static.rupeezy.in/stock_images/svgs/${result._source.sSymbol?.toUpperCase()}.svg`];
            break;
        case 'OPTFUT':
            let data2 = result._source.sExchange + ":" + result._source.sSymbol + result._source.nExpiryDate1?.slice(-2) + result._source.nExpiryDate1?.slice(2, 5) + (parseInt(String(result._source.nStrikePrice)) / 100).toString() + result._source.sOptionType;
            result["symbol"] = data2;
            result["ticker"] = data2;
            result["name"] = data2;
            result["type"] = "Options";
            result["description"] = result._source.sSymbol + " " + result._source.nExpiryDate1 + " " + (parseInt(String(result._source.nStrikePrice)) / 100).toString() + " " + result._source.sOptionType;
            result["logo_urls"] = [`https://static.rupeezy.in/stock_images/svgs/${result._source.sSymbol?.toUpperCase()}.svg`];
            break;
        case 'FUTCUR':
            result["symbol"] = "NSE" + ":" + result._source.sSecurityDesc;
            result["ticker"] = "NSE" + ":" + result._source.sSecurityDesc;
            result["name"] = "NSE" + ":" + result._source.sSecurityDesc;
            result["type"] = "Futures";
            result["description"] = result._source.sSymbol + " " + result._source.nExpiryDate1 + " FUT";
            result["exchange"] = "NSE";
            break;
        case 'OPTCUR':
            result["symbol"] = "NSE" + ":" + result._source.sSecurityDesc;
            result["ticker"] = "NSE" + ":" + result._source.sSecurityDesc;
            result["name"] = "NSE" + ":" + result._source.sSecurityDesc;
            result["type"] = "Options";
            result["description"] = result._source.sSymbol + " " + result._source.nExpiryDate1 + " " + (parseFloat(String(result._source.nStrikePrice)) / Number(result._source.DecimalLocator)).toString() + " " + result._source.sOptionType;
            result["exchange"] = "NSE";
            break;
        default:
            result["type"] = "NO IDEA";
            result["description"] = String(result._source.sSecurityDesc);
            result["type"] = String(result._source.sInstrumentName);
    }

    switch (symbolType) {
        case null:
        case 0:
            break;
        case 1: // Stocks
            if (result["type"] != "Stock") {
                return null; // Return null if the type is not "Stocks"
            }
            break;
        case 2: // Options
            if (result["type"] != "Options") {
                return null; // Return null if the type is not "Options"
            }
            break;
        case 3: // Futures 
            if (result["type"] != "Futures") {
                return null; // Return null if the type is not "Futures"
            }
            break;
        case 4: // Indices
            if (result["type"] != "Index") {
                return null; // Return null if the type is not "Index"
            }
            break;
        default:
            break;
    }

    return result;
}


export function convertTimestampToISO(timestamp: number): string {
    return new Date(timestamp * 1000).toISOString();
}